/**
 * ч.1334: «Підтримка → Статті довідки» — усі статті і питання-відповіді довідки з їхнім станом.
 *
 * Власник: «у Підтримці мають бути самі статті і питання-відповіді, юзер платформи редагує сам
 * або доручає агенту переробити». Тут — перелік з відбором; текст, правка і агент — на сторінці
 * статті (`SupportHelpArticlePage`).
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';

import { KIND_LABEL, supportHelp, type ArticleKind, type HelpArticleRow } from '@/api/supportHelp';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

type Tone = 'green' | 'amber' | 'red' | 'blue';

export function Tag({ tone, children, title }: { tone: Tone; children: React.ReactNode; title?: string }) {
  return (
    <span title={title} style={{
      display: 'inline-block', whiteSpace: 'nowrap', marginRight: '0.3rem', borderRadius: 4,
      padding: '0 0.35rem', fontSize: '0.75rem', background: `var(--badge-${tone}-bg)`,
      color: `var(--badge-${tone}-fg)`, border: `1px solid var(--badge-${tone}-bd)`,
    }}>{children}</span>
  );
}

export function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('uk-UA', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    timeZone: 'Europe/Kyiv',
  }).format(new Date(iso));
}

type State = '' | 'edited' | 'kb' | 'draft' | 'archived';

function stateTags(r: HelpArticleRow) {
  return (
    <>
      {r.status === 'archived' && <Tag tone="red">прибрано з довідки</Tag>}
      {r.kb_update && <Tag tone="amber" title="Розробники змінили цю статтю в базі знань — вирішіть, що лишити">
        оновлення з бази знань</Tag>}
      {r.kb_removed && <Tag tone="red" title="У базі знань цієї статті більше немає">прибрано з бази знань</Tag>}
      {(r.draft_status === 'QUEUED' || r.draft_status === 'RUNNING') && <Tag tone="blue">агент працює</Tag>}
      {r.draft_status === 'DONE' && <Tag tone="blue" title="Агент запропонував текст — прийміть або відхиліть">
        варіант агента</Tag>}
      {r.own ? <Tag tone="green" title="Статтю створено на платформі, база знань її не веде">власна</Tag>
        : r.version > 1 && <Tag tone="green" title="Текст змінено на платформі">змінено тут</Tag>}
    </>
  );
}

export default function SupportHelpPage() {
  const navigate = useNavigate();
  const [section, setSection] = useState('');
  const [kind, setKind] = useState<'' | ArticleKind>('');
  const [state, setState] = useState<State>('');
  const [needle, setNeedle] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQ(needle.trim()), 300);
    return () => clearTimeout(t);
  }, [needle]);

  const list = useQuery({ queryKey: ['support-help', q], queryFn: () => supportHelp.list(q || undefined) });
  const all = list.data ?? [];
  const sections = useMemo(() => Array.from(new Set(all.map((r) => r.section))), [all]);
  const rows = all.filter((r) => (!section || r.section === section) && (!kind || r.kind === kind)
    && (!state
      || (state === 'edited' && (r.version > 1 || r.own))
      || (state === 'kb' && (r.kb_update || r.kb_removed))
      || (state === 'draft' && !!r.draft_status)
      || (state === 'archived' && r.status === 'archived'))
    && (state === 'archived' || r.status !== 'archived'));
  const waiting = all.filter((r) => r.kb_update || r.kb_removed).length;

  // Порядок — за важливістю: назва і стан видно завжди, адреса сторінки — праворуч.
  const columns: ColumnDef<HelpArticleRow>[] = [
    {
      key: 'title', header: 'Назва', width: 320, sortValue: (r) => r.title,
      cell: (r) => <Link to={`/platform/support-help/${r.id}`}>{r.title}</Link>,
    },
    { key: 'state', header: 'Стан', width: 190, cell: stateTags,
      sortValue: (r) => (r.kb_update || r.kb_removed ? 0 : r.draft_status ? 1 : r.version > 1 || r.own ? 2 : 3) },
    { key: 'kind', header: 'Вид', width: 150, sortValue: (r) => KIND_LABEL[r.kind],
      cell: (r) => (r.agent_only ? `${KIND_LABEL[r.kind]}, лише агент` : KIND_LABEL[r.kind]) },
    { key: 'section', header: 'Розділ', width: 140, sortValue: (r) => r.section, cell: (r) => r.section },
    {
      key: 'edited', header: 'Змінено', width: 130, sortValue: (r) => r.edited_at ?? '',
      cell: (r) => (r.edited_at
        ? <span title={`${fmtWhen(r.edited_at)}${r.edited_by ? ` · ${r.edited_by}` : ''}`}>
          {fmtWhen(r.edited_at).slice(0, 10)}</span>
        : '—'),
    },
    {
      key: 'where', header: 'Сторінка', width: 220,
      sortValue: (r) => r.route_pattern ?? r.parent_title ?? '',
      cell: (r) => (r.kind === 'question'
        ? (r.parent_id ? <Link to={`/platform/support-help/${r.parent_id}`}>{r.parent_title}</Link> : '—')
        : (r.route_pattern ? `/${r.route_pattern}` : '—')),
    },
  ];

  return (
    <div className="card">
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>Статті довідки</h2>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          {list.isLoading ? 'завантаження…' : `${rows.length} з ${all.filter((r) => r.status !== 'archived').length}`}
        </span>
        {waiting > 0 && (
          <button type="button" className="btn-link" onClick={() => setState('kb')}>
            <Tag tone="amber">чекають рішення: {waiting}</Tag>
          </button>
        )}
      </div>
      <p style={{ margin: '0 0 0.6rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        Тексти довідки, які бачать користувачі і за якими відповідає агент підтримки. Відкрийте статтю,
        щоб виправити її самостійно або доручити агенту — він бачить усю довідку і пропонує новий текст,
        а застосовуєте ви.
      </p>
      <div className="doc-grid" style={{ marginBottom: '0.6rem' }}>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label htmlFor="sh-q">Пошук</label>
          <input id="sh-q" value={needle} onChange={(e) => setNeedle(e.target.value)}
            placeholder="слово з назви чи тексту" />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label htmlFor="sh-section">Розділ</label>
          <select id="sh-section" value={section} onChange={(e) => setSection(e.target.value)}>
            <option value="">усі розділи</option>
            {sections.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label htmlFor="sh-kind">Вид</label>
          <select id="sh-kind" value={kind} onChange={(e) => setKind(e.target.value as '' | ArticleKind)}>
            <option value="">усі</option>
            {(Object.keys(KIND_LABEL) as ArticleKind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label htmlFor="sh-state">Стан</label>
          <select id="sh-state" value={state} onChange={(e) => setState(e.target.value as State)}>
            <option value="">усі</option>
            <option value="edited">змінені на платформі</option>
            <option value="kb">чекають рішення щодо бази знань</option>
            <option value="draft">є доручення агенту</option>
            <option value="archived">прибрані з довідки</option>
          </select>
        </div>
      </div>
      <DataTable<HelpArticleRow>
        rows={rows}
        columns={columns}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Статей не знайдено"
        storageKey="platform-support-help"
        onRowDoubleClick={(r) => navigate(`/platform/support-help/${r.id}`)}
      />
    </div>
  );
}
